'use client'

import { useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useAuthStore } from '../../../../shared/platform/store'
import { authClient, isValidUserProfile } from '../../../../shared/platform/lib'
import { toast } from 'sonner'
import { setSentryUser } from '@/lib/sentry/context'
import { getSkewProtectionHeaders } from '@/lib/auth/skew-protection-headers'

export interface LoginFormState {
  isLoading: boolean
  email: string
  password: string
  showPassword: boolean
  loginError: string
  showResetForm: boolean
  resetEmail: string
  resetSent: boolean
}

/**
 * ログイン後の遷移先を安全な同一サイト相対パスに限定する。
 * `next` クエリは攻撃者が操作可能なため、外部URL・プロトコル相対・スキームを弾き、
 * 不正値は /dashboard にフォールバックする（オープンリダイレクト対策）。
 */
export function resolveSafeNextPath(rawNext: string | null): string {
  if (
    rawNext &&
    rawNext.startsWith('/') &&
    !rawNext.startsWith('//') &&
    !rawNext.includes('\\') &&
    // 制御文字（改行・CR・タブ等）を弾く。ブラウザが /\n/evil.com を
    // https://evil.com に正規化するオープンリダイレクト迂回を防止する。
    !/[\u0000-\u001f\u007f]/.test(rawNext)
  ) {
    return rawNext
  }
  return '/bt-progress'
}

export interface LoginFormActions {
  setEmail: (v: string) => void
  setPassword: (v: string) => void
  setShowPassword: (v: boolean) => void
  setLoginError: (v: string) => void
  setShowResetForm: (v: boolean) => void
  setResetEmail: (v: string) => void
  setResetSent: (v: boolean) => void
  handleEmailLogin: (e: React.FormEvent) => void
  handlePasswordReset: (e: React.FormEvent) => void
}

export function useLoginForm(): LoginFormState & LoginFormActions {
  const { setUser } = useAuthStore()
  const searchParams = useSearchParams()
  const nextPath = resolveSafeNextPath(searchParams.get('next'))

  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [showResetForm, setShowResetForm] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetSent, setResetSent] = useState(false)

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setLoginError('')

    try {
      const { error } = await authClient.forgetPassword({
        email: resetEmail,
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) {
        setLoginError(
          error.message || 'パスワードリセットに失敗しました。管理者にお問い合わせください。'
        )
      } else {
        setResetSent(true)
        toast.success('パスワードリセットメールを送信しました')
      }
    } catch {
      setLoginError('パスワードリセットに失敗しました。管理者にお問い合わせください。')
    } finally {
      setIsLoading(false)
    }
  }

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setLoginError('')

    try {
      // better-auth でメール・パスワード認証（リトライ付き）
      // 429（レート制限超過）時、better-auth は残り待ち秒数を X-Retry-After ヘッダーで返す
      // (node_modules/better-auth/dist/api/rate-limiter/index.mjs の rateLimitResponse)。
      // onError フックでレスポンスヘッダーを読み、具体的な待ち時間をユーザーに提示する。
      let retryAfterSeconds: number | null = null
      const attemptSignIn = () =>
        authClient.signIn.email(
          { email, password },
          {
            onError: (ctx) => {
              const header = ctx.response.headers.get('X-Retry-After')
              const parsed = header ? Number(header) : NaN
              if (Number.isFinite(parsed) && parsed > 0) {
                retryAfterSeconds = parsed
              }
            },
          }
        )

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('認証がタイムアウトしました')), 30000)
      })

      let lastError: unknown = null
      let data: Awaited<ReturnType<typeof authClient.signIn.email>>['data'] = null
      let error: Awaited<ReturnType<typeof authClient.signIn.email>>['error'] = null

      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const result = await Promise.race([attemptSignIn(), timeoutPromise])
          data = result.data
          error = result.error
          // better-auth からの確定レスポンス（成功・401・429 等）はリトライしても結果が変わらない。
          // ここで再送すると IP 単位のレート制限（15分30回・lib/auth/auth.ts）を
          // 1クリックで2回消費してしまいロックを早めるため、確定応答時は即break。
          // リトライ対象は Promise.race のタイムアウト・ネットワーク例外（catch節）のみに限定する。
          break
        } catch (e) {
          lastError = e
          if (attempt === 0) {
            await new Promise((r) => setTimeout(r, 2000))
          }
        }
      }

      if (error) {
        if (error.status === 429) {
          const waitText = retryAfterSeconds
            ? `約${Math.max(1, Math.ceil(retryAfterSeconds / 60))}分後に`
            : 'しばらく時間をおいてから'
          setLoginError(`ログイン試行回数が上限に達しました。${waitText}再度お試しください。`)
        } else {
          setLoginError('メールアドレスまたはパスワードが正しくありません')
        }
        return
      }

      if (lastError && !data) {
        throw lastError
      }

      if (data?.user) {
        // better-auth 認証成功 - fetch user profile via API (bypasses RLS)
        try {
          const profileController = new AbortController()
          const profileTimeout = setTimeout(() => profileController.abort(), 15000)
          const profileResponse = await fetch(
            `/api/auth/profile?email=${encodeURIComponent(email)}`,
            {
              signal: profileController.signal,
              headers: getSkewProtectionHeaders(),
            }
          ).finally(() => clearTimeout(profileTimeout))

          if (profileResponse.ok) {
            const profile = await profileResponse.json()
            if (isValidUserProfile(profile)) {
              setUser(profile)
              setSentryUser(profile)
              toast.success(`${profile.name}としてログインしました`)
              // 2026-05-17 西野氏方針 (PR #1720):
              //   強制パスワード変更画面 (/change-password) は廃止。
              //   パスワード変更はプロフィールモーダルから自由に行う。
              //   旧実装の `profile.mustChangePassword ? '/change-password' : nextPath`
              //   分岐は削除して常に nextPath へ遷移する (Codex SO 指摘・HIGH 2 完全対応)。
              window.location.href = nextPath
              return
            }
          }

          setLoginError('ユーザー情報の取得に失敗しました。管理者にお問い合わせください。')
          return
        } catch {
          setLoginError('ユーザー情報の取得に失敗しました。ネットワーク接続を確認してください。')
          return
        }
      }

      setLoginError('ログインに失敗しました。もう一度お試しください。')
    } catch (err) {
      if (err instanceof Error && err.message === '認証がタイムアウトしました') {
        setLoginError('認証がタイムアウトしました。ネットワーク接続を確認してください。')
      } else {
        setLoginError('ログインに失敗しました')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    email,
    password,
    showPassword,
    loginError,
    showResetForm,
    resetEmail,
    resetSent,
    setEmail,
    setPassword,
    setShowPassword,
    setLoginError,
    setShowResetForm,
    setResetEmail,
    setResetSent,
    handleEmailLogin,
    handlePasswordReset,
  }
}
